import { useParams } from 'react-router-dom';
import { Download, Loader2, Mail, RefreshCw, Upload } from 'lucide-react';
import { TokenChips } from '@/components/analysis/TokenChips';
import { apiErrorMessage, formatWhen, statusVariant } from '@/components/analysis/reportAnalysisUi';
import { SyncReportsButton } from '@/components/SyncReportsButton';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { useAuth } from '@/contexts/AuthContext';
import { useReportAnalysis } from '@/hooks/useReportAnalysis';
import { ANALYSIS_GRAIN_LABELS } from '@/types/reportAnalysis';

const QUERY_ORDER = Object.keys(ANALYSIS_GRAIN_LABELS);

export function AnalysisPage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const { user } = useAuth();
  const {
    startMonth,
    setStartMonth,
    endMonth,
    setEndMonth,
    brandNames,
    setBrandNames,
    competitorNames,
    setCompetitorNames,
    configLoading,
    windowError,
    inventoryQuery,
    jobsQuery,
    startMutation,
    uploadMutation,
    retryMutation,
    download,
    fileRef,
    zipName,
    setZipName,
    cells,
    months,
    byMonth,
    missingCount,
    jobs,
    busy,
  } = useReportAnalysis(profileId);

  return (
    <div className="mx-auto max-w-6xl space-y-5 bg-page px-4 py-6 min-h-full">
      <div>
        <h1 className="text-headline-md font-semibold text-foreground">Analysis</h1>
        <p className="mt-1 max-w-3xl text-sm text-foreground-muted">
          Generate a Search Ads diagnosis (Excel deep-dive + A4 PDF briefing) from archived S3
          reports. You can leave this tab — we email both files to{' '}
          <span className="font-medium text-foreground">{user?.email || 'your account email'}</span>{' '}
          when the job finishes.
        </p>
      </div>

      <Card className="space-y-5 p-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
              Start month
            </label>
            <Input type="month" value={startMonth} onChange={(event) => setStartMonth(event.target.value)} />
          </div>
          <div>
            <label className="mb-1.5 block text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
              End month
            </label>
            <Input type="month" value={endMonth} onChange={(event) => setEndMonth(event.target.value)} />
          </div>
        </div>
        {windowError ? <p className="text-sm text-error-ink">{windowError}</p> : null}

        <div className="grid gap-4 md:grid-cols-2">
          <TokenChips
            label="Brand names"
            tokens={brandNames}
            onChange={setBrandNames}
            placeholder="e.g. your brand, product line — Enter to add"
            disabled={configLoading}
          />
          <TokenChips
            label="Competitor names"
            tokens={competitorNames}
            onChange={setCompetitorNames}
            placeholder="e.g. rival brands — Enter to add"
            disabled={configLoading}
          />
        </div>

        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-body-md font-semibold text-foreground">S3 coverage</h2>
            <div className="flex items-center gap-2">
              <SyncReportsButton
                profileId={profileId}
                className="inline-flex h-8 items-center rounded-lg border border-border px-3 text-body-sm font-medium text-foreground hover:border-brand"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void inventoryQuery.refetch()}
                disabled={inventoryQuery.isFetching}
              >
                <RefreshCw className={`h-4 w-4 ${inventoryQuery.isFetching ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          {inventoryQuery.isLoading ? (
            <p className="text-sm text-foreground-muted">Checking archives…</p>
          ) : inventoryQuery.error ? (
            <p className="text-sm text-error-ink">
              {apiErrorMessage(inventoryQuery.error, 'Could not list S3 archives')}
            </p>
          ) : cells.length === 0 ? (
            <p className="text-sm text-foreground-muted">
              No archives listed for this window. Sync reports first, then wait for gzip files to
              land on S3.
            </p>
          ) : (
            <TableWrap>
              <Table compact>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    {QUERY_ORDER.map((queryId) => (
                      <TableHead key={queryId}>{ANALYSIS_GRAIN_LABELS[queryId]}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <tbody>
                  {months.map((month) => {
                    const row = byMonth.get(month) ?? [];
                    return (
                      <TableRow key={month}>
                        <TableCell className="font-medium">{month}</TableCell>
                        {QUERY_ORDER.map((queryId) => {
                          const cell = row.find((item) => item.queryId === queryId);
                          const count = cell?.objectCount ?? 0;
                          return (
                            <TableCell key={queryId}>
                              <Badge variant={count > 0 ? 'success' : 'warning'}>
                                {count > 0 ? `${count} object${count === 1 ? '' : 's'}` : 'Missing on S3'}
                              </Badge>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          )}
          {missingCount > 0 ? (
            <p className="mt-2 text-sm text-foreground-muted">
              {missingCount} grain/month cell{missingCount === 1 ? '' : 's'} missing on S3. Sync
              reports and wait for archives — this job does not read dashboard fact tables.
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          <Button
            disabled={!profileId || Boolean(windowError) || busy || configLoading}
            onClick={() => startMutation.mutate()}
          >
            {startMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Generate analysis
          </Button>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".zip,application/zip"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                setZipName(file.name);
                uploadMutation.mutate(file);
              }}
            />
            <Button
              variant="secondary"
              disabled={!profileId || Boolean(windowError) || busy || configLoading}
              onClick={() => fileRef.current?.click()}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {zipName && uploadMutation.isPending ? zipName : 'Upload Console CSV zip'}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-body-md font-semibold text-foreground">Job history</h2>
          <Button variant="ghost" size="sm" onClick={() => void jobsQuery.refetch()}>
            <RefreshCw className={`h-4 w-4 ${jobsQuery.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        {jobsQuery.isLoading ? (
          <p className="text-sm text-foreground-muted">Loading jobs…</p>
        ) : jobs.length === 0 ? (
          <p className="text-sm text-foreground-muted">No diagnosis jobs yet for this account.</p>
        ) : (
          <TableWrap>
            <Table compact>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Files</TableHead>
                </TableRow>
              </TableHeader>
              <tbody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <div className="font-medium">
                        {job.startMonth} – {job.endMonth}
                      </div>
                      <div className="text-body-sm text-foreground-muted">
                        {job.source === 's3_upload' ? 'Console CSV upload' : 'S3 archives'}
                      </div>
                      {job.error ? <div className="mt-1 text-body-sm text-error-ink">{job.error}</div> : null}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(job.status)}>{job.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant={statusVariant(job.emailStatus)}>{job.emailStatus || '—'}</Badge>
                        {job.status === 'succeeded' && job.emailStatus === 'failed' ? (
                          <Button
                            variant="link"
                            size="sm"
                            disabled={retryMutation.isPending}
                            onClick={() => retryMutation.mutate(job.id)}
                          >
                            Retry email
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-foreground-muted">
                      {formatWhen(job.createdAt)}
                    </TableCell>
                    <TableCell>
                      {job.status === 'succeeded' ? (
                        <div className="flex flex-wrap gap-2">
                          {job.xlsxReady ? (
                            <Button variant="secondary" size="sm" onClick={() => void download(job, 'xlsx')}>
                              <Download className="h-3.5 w-3.5" />
                              Excel
                            </Button>
                          ) : null}
                          {job.pdfReady ? (
                            <Button variant="secondary" size="sm" onClick={() => void download(job, 'pdf')}>
                              <Download className="h-3.5 w-3.5" />
                              PDF
                            </Button>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-foreground-muted">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
